import {
  Component,
  computed,
  effect,
  OnDestroy,
  OnInit,
  signal,
  untracked,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  ConditionOption,
  ListingFilters,
} from '../../domain/models/listing.model';
import { CategoriesRepositoryQuery } from '../../domain/repositories/categories.repository';
import { Subscription } from 'rxjs';
import { GetCategoryUsecase } from '../../data/repositories/categories/usecases/get-category.usecase';
import {
  CategoryModel,
  GroupAttributeModel,
} from '../../domain/models/category.model';
import { CheckboxMultiselectFormFieldComponent } from '../shared/components/checkbox-multiselect-form-field/checkbox-multiselect-form-field.component';
import { ActivatedRoute, Router } from '@angular/router';
import { ListingFiltersParserUtility } from '../../data/utilities/listing-filters-parser/listing-filters-parser.utility';
import { cleanObject } from '../../lib/clean-object';
import { attributeOptionsToBaseOptions } from '../../lib/attribute-options-to-base-options';
import { listingConditionOptions } from '../shared/constants/listing-condition-options';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumber } from 'primeng/inputnumber';
import { Select } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { FormFieldComponent } from '../shared/components/form-field/form-field.component';
import { RegionCitySelectorComponent } from '../shared/components/region-city-selector/region-city-selector.component';
import { HomeBannersWrapperComponent } from '../shared/components/home-banners-wrapper/home-banners-wrapper.component';
import { Skeleton } from 'primeng/skeleton';
import { MultiSelectModule } from 'primeng/multiselect';
import { MetaPixelService } from '../../services/meta-pixel.service';

@Component({
  selector: 'otw-search',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CheckboxMultiselectFormFieldComponent,
    InputTextModule,
    InputNumber,
    Select,
    ButtonModule,
    FormFieldComponent,
    RegionCitySelectorComponent,
    HomeBannersWrapperComponent,
    Skeleton,
    MultiSelectModule,
  ],
  templateUrl: './search.component.html',
  styles: ``,
})
export class SearchComponent implements OnInit, OnDestroy {
  subscriptions: Subscription[] = [];
  categories = this.categoriesRepositoryQuery.categories;
  categoryOptions = computed(() => {
    return this.categories().map((category) => ({
      label: category.name,
      value: category.slug,
    }));
  });
  selectedCategory = signal<CategoryModel | null>(null);
  attributes = signal<GroupAttributeModel[]>([]);
  searchForm = this.fb.group({
    slug: this.fb.control<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    price_min: this.fb.control<number | undefined>(undefined, {
      nonNullable: true,
    }),
    price_max: this.fb.control<number | undefined>(undefined, {
      nonNullable: true,
    }),
    regionCity: this.fb.group({
      region: this.fb.control<string | undefined>(undefined, {
        nonNullable: true,
      }),
      city: this.fb.control<string | undefined>(undefined, {
        nonNullable: true,
      }),
    }),
    condition: this.fb.control<ConditionOption[]>(
      [ConditionOption.NEW, ConditionOption.USED],
      {
        nonNullable: true,
      }
    ),
    attributes: this.fb.record<{ [key: string]: string | number }>({}),
  });
  isCategoryLoading = signal(true);

  conditionOptions = listingConditionOptions;

  toBaseOptions = attributeOptionsToBaseOptions;

  constructor(
    private fb: FormBuilder,
    private categoriesRepositoryQuery: CategoriesRepositoryQuery,
    private getCategoryUsecase: GetCategoryUsecase,
    private listingFiltersParserUtility: ListingFiltersParserUtility,
    private router: Router,
    private activeRoute: ActivatedRoute,
    private metaPixel: MetaPixelService
  ) {}

  categoriesChangeEffect = effect(() => {
    this.categories(); // need this so the effect knows when to activate again
    untracked(() => {
      this._setDefaultSelectedCategory();
    });
  });

  private _setDefaultSelectedCategory = () => {
    const categories = this.categories();
    if (categories.length < 1) return;
    // if the slug already has a value we don't want to change it
    if (this.searchForm.controls.slug.value) return;
    const querySlug = this.activeRoute.snapshot.queryParamMap.get('slug');
    const firstCategorySlug = categories[0].slug;
    const defaultSlug = querySlug || firstCategorySlug;
    this.searchForm.patchValue({ slug: defaultSlug });
  };

  updateAttributeFields(attributes: GroupAttributeModel[]) {
    this.searchForm.controls.attributes.reset();
    attributes.forEach((attribute) => {
      this.searchForm.controls.attributes.addControl(
        attribute.id,
        this.fb.control(null)
      );
    });
  }

  private async _onCategoryChange(selectedCategorySlug: string) {
    this.isCategoryLoading.set(true);
    const result = await this.getCategoryUsecase.execute(selectedCategorySlug);

    this.isCategoryLoading.set(false);
    if (result.isErr()) {
      this.router.navigate(['/not-found']);
      return;
    }

    const attributes = result.unwrap().groups.flatMap((g) => g.attributes);

    this.updateAttributeFields(attributes);
    this.attributes.set(attributes);
    this.selectedCategory.set(result.unwrap());
  }
  private _toListingFilters(): ListingFilters {
    const form = this.searchForm.value;
    const attrs = cleanObject(form.attributes || {});
    const { regionCity, ...rest } = form;

    return {
      ...rest,
      region: regionCity?.region,
      city: regionCity?.city,
      attributes: attrs,
      slug: this.searchForm.controls.slug.value,
    };
  }

  onSubmit() {
    this.searchForm.markAllAsTouched();
    if (this.searchForm.invalid) return;

    const filters = this._toListingFilters();

    // 👇 Meta Pixel track
    this.metaPixel.track('Search', {
      location: filters.city || filters.region || undefined,
      min_price: filters.price_min || undefined,
      max_price: filters.price_max || undefined,
    });

    const query = this.listingFiltersParserUtility.toQuery({
      start: 0,
      end: 10,
      filters,
    });
    this.router.navigateByUrl(`listing/results?${query}`);
  }

  ngOnInit(): void {
    // subscribe to category change
    const categorySlugSub =
      this.searchForm.controls.slug.valueChanges.subscribe((value) => {
        this._onCategoryChange(value);
      });
    this.subscriptions.push(categorySlugSub);
    //
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}
